/**
 * ReferenceDocumentModal — upload files to a project's Reference Documents, or change which kit item
 * a file answers (docs/REFERENCE_DOCUMENTS.md). Mounted once in OutlineView; opened by the
 * 'open-reference-doc-modal' event:
 *   { projectId, factId? }   upload (factId preselects the item)
 *   { projectId, docId }     change the item an existing file answers
 *
 * Uploading never ticks the kit item: the person ticking it opens the file and decides (Matt,
 * 2026-10-13).
 */

import { useEffect, useState } from 'react'
import { ctx } from '../outline/ctx.js'
import { confirmDialog } from './Dialog.jsx'
import { reopenWarning, reportReopened } from './ReferenceDocumentsSection.jsx'
import {
  listReferenceDocuments, uploadReferenceDocument, updateReferenceDocument, announceReferenceDocumentsChanged,
} from '../services/referenceDocuments.js'

function who() {
  const m = ctx.currentMember
  return { memberId: m?.id || null, memberName: m?.fields?.['Member Name'] || m?.fields?.['Name'] || null }
}

export default function ReferenceDocumentModal() {
  const [open, setOpen] = useState(null)        // { projectId, factId?, docId? }
  const [facts, setFacts] = useState(null)
  const [factId, setFactId] = useState('')
  const [files, setFiles] = useState([])
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState('')
  const [error, setError] = useState('')
  const [dragOver, setDragOver] = useState(false)

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      if (!d.projectId) return
      setOpen(d); setFiles([]); setError(''); setProgress(''); setBusy(false); setFacts(null)
      setFactId(d.factId || '')
      listReferenceDocuments(d.projectId).then(r => {
        setFacts(r.facts || [])
        if (d.docId) { const doc = (r.documents || []).find(x => x.id === d.docId); setFactId(doc && doc.fact_id || '') }
      }).catch(err => setError(String(err.message || err)))
    }
    window.addEventListener('open-reference-doc-modal', onOpen)
    return () => window.removeEventListener('open-reference-doc-modal', onOpen)
  }, [])

  useEffect(() => {
    if (!open) return
    function onKey(e) { if (e.key === 'Escape' && !busy) setOpen(null) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, busy])

  if (!open) return null
  const retag = !!open.docId
  const canSubmit = !busy && (retag || files.length > 0)

  async function submit() {
    if (!canSubmit) return
    setBusy(true); setError('')
    try {
      if (retag) {
        // Moving the file off a ticked item that has no other file reopens it: say so first.
        const c = ctx.refDocCache && ctx.refDocCache[open.projectId]
        const doc = c && (c.documents || []).find(d => d.id === open.docId)
        const warn = doc && (doc.fact_id || '') !== (factId || '') ? reopenWarning(open.projectId, doc) : null
        if (warn && !(await confirmDialog({ title: 'Move this file?', message: warn, confirmLabel: 'Move and reopen', danger: true }))) { setBusy(false); return }
        const r = await updateReferenceDocument(open.docId, { factId: factId || null })
        reportReopened(r && r.reopened)
      } else {
        for (let i = 0; i < files.length; i++) {
          setProgress(`Uploading ${i + 1} of ${files.length}…`)
          await uploadReferenceDocument({ projectId: open.projectId, file: files[i], factId: factId || null, ...who() })
        }
      }
      announceReferenceDocumentsChanged(open.projectId)
      setOpen(null)
    } catch (e) {
      setError(String(e && e.message || e)); setBusy(false); setProgress('')
    }
  }

  function addFiles(list) { setFiles(prev => prev.concat(Array.from(list || []))) }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onMouseDown={e => { if (e.target === e.currentTarget && !busy) setOpen(null) }}>
      <div data-modal-id="ReferenceDocumentModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 460, maxWidth: 'calc(100vw - 32px)', padding: '20px 20px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>
            {retag ? 'Which kit item does this file answer?' : 'Upload reference documents'}
          </span>
          <button onClick={() => !busy && setOpen(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>
        <div style={help}>
          {retag ? 'The file shows on that item in every milestone kit that needs it.' : 'Files we received for this project: surveys, existing drawings, photos, reports. Uploading does not tick the kit item. Tick it yourself once you have looked at the file.'}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 14 }}>
          <div>
            <label style={label}>Answers kit item</label>
            <select style={input} value={factId} disabled={busy || facts === null} onChange={e => setFactId(e.target.value)}>
              <option value="">{facts === null ? 'Loading…' : 'None, just keep it on the project'}</option>
              {(facts || []).map(f => <option key={f.id} value={f.id}>{f.name.length > 90 ? f.name.slice(0, 88) + '…' : f.name}</option>)}
            </select>
          </div>

          {!retag && (
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files) }}
              style={{ border: '0.5px dashed ' + (dragOver ? 'var(--aj-accent)' : 'var(--aj-border-mid)'), borderRadius: 6, padding: '14px 12px', textAlign: 'center', background: dragOver ? 'var(--aj-bg-subtle)' : 'transparent' }}>
              <label style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', cursor: busy ? 'default' : 'pointer' }}>
                Drop files here, or <span style={{ textDecoration: 'underline' }}>choose files</span>
                <input type="file" multiple disabled={busy} style={{ display: 'none' }} onChange={e => { addFiles(e.target.files); e.target.value = '' }} />
              </label>
              {files.length > 0 && (
                <div style={{ marginTop: 10, textAlign: 'left' }}>
                  {files.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--aj-text)', padding: '2px 0' }}>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                      <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 11, color: 'var(--aj-text-muted)' }}>{fmtSize(f.size)}</span>
                      {!busy && <button onClick={() => setFiles(files.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 14, padding: 0 }}>×</button>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 4 }}>
            {progress && <span style={{ flex: 1, fontSize: 11.5, color: 'var(--aj-text-muted)' }}>{progress}</span>}
            <button onClick={() => !busy && setOpen(null)} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
            <button onClick={submit} disabled={!canSubmit} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: canSubmit ? '#07272D' : 'var(--aj-bg-muted)', color: canSubmit ? '#fff' : 'var(--aj-text-muted)', border: 'none', borderRadius: 4, cursor: canSubmit ? 'pointer' : 'default' }}>
              {retag ? 'Save' : files.length > 1 ? `Upload ${files.length} files` : 'Upload'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export function fmtSize(n) {
  if (!Number.isFinite(n)) return ''
  if (n < 1024) return n + ' B'
  if (n < 1024 * 1024) return Math.round(n / 1024) + ' KB'
  return (n / 1024 / 1024).toFixed(1) + ' MB'
}

const label = { display: 'block', fontFamily: '"DM Sans",sans-serif', fontSize: 10.5, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginBottom: 5 }
const input = { width: '100%', boxSizing: 'border-box', padding: '6px 8px', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text)', background: 'var(--aj-bg-surface)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4 }
const help = { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', lineHeight: 1.45 }
